import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { Image, Spinner } from "react-bootstrap";
import './index.css';

const Loader = () => (
  <div className="iframe-loader">
    <Spinner animation="border" role="status" />
    <span>Loading video...</span>
  </div>
);

const YouTubeModal = ({ videoId, onClose }) => {
  return (
    <div className="youtube-modal-backdrop" onClick={onClose}>
      <div className="youtube-modal-container" onClick={e => e.stopPropagation()}>
        <div className="youtube-modal-header">
          <h5>Video Player</h5>
          <button className="youtube-modal-close" onClick={onClose}>×</button>
        </div>
        <div className="youtube-modal-body">
          <div className="youtube-iframe-container">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&playsinline=1`}
              allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
              title="YouTube Video"
              frameBorder="0"
              allowFullScreen
              className="youtube-video"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
};

const VideoCarousel = ({ title = "TESTIMONIALS", subheading = "Borrower Videos" }) => {
  const carouselRef = useRef(null);

  const responsive = {
    superLargeDesktop: { breakpoint: { max: 4000, min: 3000 }, items: 4, slidesToSlide: 3 },
    desktop: { breakpoint: { max: 3000, min: 1024 }, items: 4, slidesToSlide: 3 },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 2, slidesToSlide: 2 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1, slidesToSlide: 1 }
  };

  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [videoData, setVideoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function fetchVideoData() {
      try {
        setLoading(true);
        const response = await axios.get(process.env.REACT_APP_WHYSUNWEST_VIDEO_URL);
        const responseData = response.data;

        let formattedData = [];

        if (Array.isArray(responseData)) {
          formattedData = responseData.map(item => ({
            id: item.id,
            title: item.title || "",
            description: item.description || "",
            thumbnail: item.thumbnail || `https://i.ytimg.com/vi/${item.id}/mqdefault.jpg`
          }));
        } else if (responseData?.data?.items?.length) {
          formattedData = responseData.data.items.map(item => {
            let videoId = item?.contentDetails?.videoId ||
                          item?.snippet?.resourceId?.videoId;
            if (!videoId) return null;

            return {
              id: videoId,
              title: item.snippet?.title || "Untitled Video",
              description: item.snippet?.description || "",
              thumbnail: item.snippet?.thumbnails?.medium?.url ||
                         item.snippet?.thumbnails?.default?.url ||
                         `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`
            };
          }).filter(Boolean);
        }

        if (!formattedData.length) throw new Error("No video data available.");

        setVideoData(formattedData);
      } catch (err) {
        console.error("Error fetching video data:", err);
        setError("Failed to load videos. Please try again later.");
      } finally {
        setLoading(false);
      }
    }

    fetchVideoData();
  }, []);

  const handleThumbnailClick = (id) => setCurrentVideoId(id);
  const handleCloseModal = () => setCurrentVideoId(null);

  const handleArrow = (direction) => {
    if (!carouselRef.current) return;
    if (direction === 'left') {
      carouselRef.current.previous();
    } else {
      carouselRef.current.next();
    }
  };

  if (loading) {
    return (
      <div className="gallery-section-container">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="gallery-section-container">
        <div className="error-container">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-section-container">
      <div className="top-section">
        <div className="table-title broker-videos-title">
          {subheading && <h3>{subheading}</h3>}
          {title && <h4>{title}</h4>}
        </div>
      </div>

      <div className="custom-arrows-div">
        <button 
          className="slider-custom-arrows arrow-left"
          onClick={() => handleArrow('left')}
          aria-label="Previous slide"
        >
          <span aria-hidden="true">&#10094;</span>
        </button>
        <button 
          className="slider-custom-arrows arrow-right"
          onClick={() => handleArrow('right')}
          aria-label="Next slide"
        >
          <span aria-hidden="true">&#10095;</span>
        </button>
      </div>

      <div className="video-grid">
        <Carousel
          ref={carouselRef}
          swipeable
          draggable
          showDots={false}
          responsive={responsive}
          infinite={false}
          centerMode={false}
          autoPlaySpeed={10000}
          keyBoardControl
          autoPlay={false}
          customTransition="all .5s"
          containerClass="carousel-container"
          dotListClass="custom-dot-list-style"
          itemClass="carousel-item-padding-40-px"
        >
          {videoData.map(item => (
            <div key={item.id} className="thumbnail-container">
              <div className="image-wrapper">
                <Image
                  className="thumbnail-image"
                  src={item.thumbnail}
                  onClick={() => handleThumbnailClick(item.id)}
                  draggable="false"
                  width="320"
                  height="180"
                  alt={item.title}
                />
              </div>
            </div>
          ))}
        </Carousel>
      </div>

      {currentVideoId && (
        <YouTubeModal
          videoId={currentVideoId}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default VideoCarousel;
